import React from "react";
import { type NextPage } from "next";
import { Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import { CenteredQuote } from "components/quotes";
import {
  SyncTemplatesAnimation,
  JourneysAnimation,
  InsightsAnimation,
  TraitsAnimation,
  SplitsAnimationWithBg,
} from "components/animations";
import { FeatureVideo, HeroAnimation } from "components/platform/customer-studio";
import { DestinationMarquee, ComposableCdpFeaturesPlate, ProductDemo } from "components/content";
import { AuraHalfHero } from "components/hero";
import { AuraPlate, SolidPlate } from "ui/plates";
import { TextColumn, TextTitle } from "ui/text";
import { Button } from "ui/button";
import { Diptych, DiptychColumn } from "ui/diptych";
import { integrationsCount } from "../../constants/integrations";

const CustomerStudio: NextPage<LandingPageProps> = ({
  page,
  announcementBanner,
  destinations,
  defaultLogos,
  customers,
}) => {
  const petsmart = getSafeCustomer(customers, "petsmart");
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");

  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <AuraHalfHero
        variant="blue"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        media={<HeroAnimation />}
        mediaWrapperProps={{
          mt: { base: 0, md: "big", lg: 0 },
          pb: { base: 0, md: "huge", lg: 0 },
        }}
      />

      <StackedLogos
        customers={defaultLogos}
        my={0}
        mt={{ base: "big", md: "biggest", lg: "small" }}
        pb={sectionSpacingY}
      />

      <SolidPlate bgColor="new.blue.950" spotlightColor="new.blue.900" theme="dark">
        <TextTitle
          align="center"
          size="xl"
          theme="dark"
          chip={{ text: "Warehouse-native audience building" }}
          title="Build and use audiences, all on your own"
          maxW="700px"
        />

        <FeatureVideo />

        <SimpleGrid columns={{ base: 1, md: 3 }} gap="big" mt="bigger">
          <TextColumn
            theme="dark"
            title={
              <>
                Personalize emails and websites{" "}
                <Text as="span" fontWeight="normal">
                  to deliver consistent experiences to audiences across channels.
                </Text>
              </>
            }
          />
          <TextColumn
            theme="dark"
            title={
              <>
                Target your best prospects{" "}
                <Text as="span" fontWeight="normal">
                  or suppress less valuable audiences on advertising platforms like Meta, Google, and more.
                </Text>
              </>
            }
          />
          <TextColumn
            theme="dark"
            title={
              <>
                Set up, execute, and measure A/B tests{" "}
                <Text as="span" fontWeight="normal">
                  and hold-out groups on your own, but with the accuracy of an analytics team.
                </Text>
              </>
            }
          />
        </SimpleGrid>

        <CenteredQuote
          theme="dark"
          my={sectionSpacingY}
          maxW="860px"
          quoteText="We’re powering thousands of audiences and over four billion emails every year, and that’s not even factoring in what we do across other channels."
          author={{
            name: "Tino Tomasone",
            job: "Sr. Manager, Database & Audience Management",
          }}
          company={{
            name: "PetSmart",
            logo: petsmart.logoUrl,
          }}
          cta={{ href: "/customers/petsmart", text: "Read customer story" }}
        />

        <ProductDemo theme="dark" />
      </SolidPlate>

      <Section my={0} py={sectionSpacingY}>
        <TextTitle
          align="center"
          size="lg"
          maxW="860px"
          descriptionMaxW="640px"
          chip={{ text: "Customer Studio features" }}
          title="Create audiences without limitations"
          description="Leverage every data point you have by activating data directly from your cloud data warehouse. Don’t limit yourself to the rigid structures of traditional Customer Data Platforms."
        />

        <Flex flexDir="column" gap="huge" mt={{ base: "biggest", md: "huge" }}>
          <Diptych
            direction="left"
            media={<SyncTemplatesAnimation />}
            chip={{ text: "Activation" }}
            title="Sync your audiences to any CRM, email, or ad platform"
            description={`Sync updated audiences and customer profiles to ${integrationsCount}+ tools. Personalize and target your marketing across all of your channels with all of your data.`}
            actions={
              <Button hasRightChevron href="/integrations">
                See Hightouch integrations
              </Button>
            }
          />

          <Diptych
            id="journeys"
            direction="right"
            media={<JourneysAnimation />}
            chip={{ text: "Journeys" }}
            title="Orchestrate complex customer journeys"
            description="Easily prioritize campaigns across your audiences. Ensure that customers experience cohesive marketing and get the right message at the right time."
            actions={
              <Button hasRightChevron href="/blog/journeys-in-hightouch">
                Explore Journeys
              </Button>
            }
          />

          <Diptych
            id="splits"
            direction="left"
            media={<SplitsAnimationWithBg />}
            chip={{ text: "Splits" }}
            title="Run multivariate testing across channels"
            description="Build randomized audience splits directly within Hightouch to centrally manage your multivariate testing across channels. Never upload a testing CSV again."
            actions={
              <Button hasRightChevron href="/blog/audience-splits">
                Learn about Splits
              </Button>
            }
          />
        </Flex>
      </Section>

      <AuraPlate condensed variant="gray">
        <CenteredQuote
          maxW="800px"
          quoteText="With Customer Studio, we’re able to build more granular audiences much faster, and we can understand the logic behind them."
          author={{ name: "David Cohen", job: "Chief Data Officer" }}
          company={{ name: "Weight Watchers", logo: weightWatchers.logoUrl }}
          cta={{ href: "/customers/weight-watchers", text: "Read customer story" }}
        />
      </AuraPlate>

      <Section my={0} py={sectionSpacingY}>
        <DiptychColumn>
          <Diptych
            id="traits"
            direction="left"
            media={<TraitsAnimation />}
            chip={{ text: "Traits" }}
            title="Create new attributes for personalization in seconds"
            description="Easily define new calculated fields for personalization and segmentation like ‘Last Page Viewed’ or ‘Lifetime Value’ without code."
            actions={
              <Button hasRightChevron href="/blog/introducing-hightouch-traits">
                Explore Custom Traits
              </Button>
            }
          />

          <Diptych
            direction="right"
            media={<InsightsAnimation />}
            chip={{ text: "Insights" }}
            title="Build the right audience every time"
            description="Explore audience sizes and makeup as you build them. Visualize the distribution of user characteristics within audiences, or check audience overlaps to better understand who you are targeting."
            actions={
              <Button hasRightChevron href="/blog/audience-breakdowns">
                Explore Audience Breakdowns
              </Button>
            }
          />
        </DiptychColumn>
      </Section>

      <ComposableCdpFeaturesPlate
        bgColor="new.blue.900"
        theme="dark"
        title="Customer Studio is powered by your cloud data warehouse"
      />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/platform/customer-studio", {
  loadDefaultLogos: true,
  loadDestinations: "all",
  loadCustomers: ["weight-watchers", "petsmart"],
});

export default CustomerStudio;
